import React, { useState } from 'react'
import { CheckIcon } from '@heroicons/react/20/solid'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function PricingPlans({ products, current_plan, edit_path_template }) {
  const [billingInterval, setBillingInterval] = useState(
    current_plan?.interval === 'month' ? 'month' : 'year'
  )

  // If there's only one product, show it as a single card
  const isSinglePlan = products.length === 1

  return (
    <div className="space-y-6">
      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center p-1 bg-gray-100 rounded-xl">
          <button
            type="button"
            onClick={() => setBillingInterval('month')}
            className={classNames(
              'px-4 py-2 text-sm font-medium rounded-lg transition-all',
              billingInterval === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('year')}
            className={classNames(
              'px-4 py-2 text-sm font-medium rounded-lg transition-all',
              billingInterval === 'year'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Yearly
            <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className={classNames(
        'grid gap-6',
        isSinglePlan ? 'max-w-lg mx-auto' : 'lg:grid-cols-3'
      )}>
        {products.map((product) => {
          const price = billingInterval === 'month' ? product.month_price : product.year_price
          if (!price) return null

          const isCurrentPlan = price.is_subbed
          const editPath = edit_path_template.replace('{price_id}', price.id)

          return (
            <div
              key={product.id}
              className={classNames(
                'relative bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all hover:shadow-md',
                isCurrentPlan ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-200'
              )}
            >
              {/* Current Plan Badge */}
              {isCurrentPlan && (
                <div className="absolute top-0 right-0">
                  <div className="bg-indigo-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Current Plan
                  </div>
                </div>
              )}

              <div className="p-6 sm:p-8">
                {/* Plan Name & Description */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                  {product.description && (
                    <p className="mt-2 text-sm text-gray-500">{product.description}</p>
                  )}
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">
                      {price.human_amount}
                    </span>
                    <span className="ml-1 text-lg text-gray-500">
                      /{billingInterval === 'month' ? 'mo' : 'yr'}
                    </span>
                  </div>
                  {billingInterval === 'year' && product.month_price && (
                    <p className="mt-1 text-sm text-gray-500">
                      <span className="line-through">{product.month_price.human_amount}/mo</span>
                      <span className="ml-2 text-green-600 font-medium">Save ${((product.month_price.unit_amount * 12 - price.unit_amount) / 100).toFixed(0)}/year</span>
                    </p>
                  )}
                </div>

                {/* CTA Button */}
                {isCurrentPlan ? (
                  <div className="w-full py-3 px-4 rounded-xl bg-indigo-50 text-indigo-700 font-semibold text-center">
                    <span className="flex items-center justify-center gap-2">
                      <CheckIcon className="w-5 h-5" />
                      Your Current Plan
                    </span>
                  </div>
                ) : (
                  <a
                    href={editPath}
                    className="block w-full py-3 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-center transition-colors"
                  >
                    {current_plan ? 'Switch to this plan' : 'Get started'}
                  </a>
                )}

                {/* Features */}
                {product.feature_list && product.feature_list.length > 0 && (
                  <div className="mt-8">
                    <p className="text-sm font-semibold text-gray-900 mb-4">What's included:</p>
                    <ul className="space-y-3">
                      {product.feature_list.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center">
                            <CheckIcon className="h-3.5 w-3.5 text-indigo-600" />
                          </div>
                          <span className="text-sm text-gray-600">{feature.trim()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Trust badges */}
      <div className="flex flex-wrap items-center justify-center gap-6 pt-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
          Secure payment
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          Cancel anytime
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
          </svg>
          No hidden fees
        </div>
      </div>
    </div>
  )
}
